import { Alert } from "@/components/Alert";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { PageHeader } from "@/components/PageHeader";
import { Tabs } from "@/components/Tabs";
import { ProviderConflictPanel } from "@/pages/Providers/ProviderConflictPanel";
import { ProviderHistory } from "@/pages/Providers/ProviderHistory";
import { providerGroups } from "@/pages/Providers/fields";
import { displayDate, displayValue } from "@/pages/Providers/display";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type { Catalogs, DetailTab, Provider } from "@/pages/Providers/types";

interface ProviderDetailProps {
    provider: Provider;
    catalogs: Catalogs;
    tab: DetailTab;
    onTab: (tab: DetailTab) => void;
    onClose: () => void;
    onEdit: (provider: Provider) => void;
    onStatus: (provider: Provider) => void;
    onSaved: (provider: Provider) => void;
    onView: (id: number) => void;
    allowActions: boolean;
}

export const ProviderDetail = ({
    provider,
    catalogs,
    tab,
    onTab,
    onClose,
    onEdit,
    onStatus,
    onSaved,
    onView,
    allowActions,
}: ProviderDetailProps) => {
    const { t, language } = useProviderContext();
    return (
        <div className="space-y-4">
            <Button
                variant="ghost"
                size="sm"
                icon="arrowLeft"
                onClick={onClose}
            >
                {t("workspace.back_list")}
            </Button>
            <PageHeader
                compact
                title={provider.name}
                subtitle={`${t("providers.fields.id")}: ${provider.id} · ${provider.tax_id}`}
                action={
                    <div className="flex flex-wrap gap-2">
                        {allowActions && (
                            <Button
                                variant="primary"
                                onClick={() => onEdit(provider)}
                            >
                                {t("providers.ui.edit")}
                            </Button>
                        )}
                        {allowActions && (
                            <Button
                                variant="secondary"
                                onClick={() => onStatus(provider)}
                            >
                                {t(
                                    `providers.ui.${Number(provider.is_active) === 1 ? "disable" : "enable"}`
                                )}
                            </Button>
                        )}
                    </div>
                }
            />
            <div className="space-y-5">
                <div className="flex flex-wrap gap-2">
                    <Badge
                        variant={
                            Number(provider.is_active) === 1
                                ? "success"
                                : "neutral"
                        }
                    >
                        {t(
                            `providers.filters.state_${Number(provider.is_active) === 1 ? "active" : "inactive"}`
                        )}
                    </Badge>
                    <Badge variant={provider.bc_present ? "info" : "neutral"}>
                        {t(
                            `providers.filters.bc_${provider.bc_present ? "linked" : "unlinked"}`
                        )}
                    </Badge>
                    {Number(provider.payment_blocked) === 1 && (
                        <Badge variant="warning">
                            {t("providers.ui.payment_blocked")}
                        </Badge>
                    )}
                    {provider.conflict_count > 0 && (
                        <Badge
                            variant={
                                provider.conflict_severity === "blocking"
                                    ? "danger"
                                    : "warning"
                            }
                        >
                            {t("providers.ui.pending_conflicts", {
                                count: provider.conflict_count,
                            })}
                        </Badge>
                    )}
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg border border-slate-200 bg-box p-4 text-xs text-slate-500 dark:border-white/10 dark:text-zinc-400">
                    <p>
                        {t("providers.fields.id")}: {provider.id}
                    </p>
                    <p>
                        {t("providers.fields.bc_code")}:{" "}
                        {provider.bc_code || t("providers.ui.not_available")}
                    </p>
                    <p>
                        {t("providers.fields.registered_at")}:{" "}
                        {displayDate(provider.registered_at, language)}
                    </p>
                    {provider.last_change && (
                        <p>
                            {t("providers.ui.last_change")}:{" "}
                            {provider.last_change.actor_name} ·{" "}
                            {displayDate(
                                provider.last_change.created_at,
                                language
                            )}
                        </p>
                    )}
                </div>
                <Tabs<DetailTab>
                    variant="underline"
                    full={false}
                    activeTab={tab}
                    onChange={onTab}
                    syncUrl={false}
                    tabs={(["details", "conflicts", "history"] as const).map(
                        (id) => ({ id, label: t(`providers.ui.${id}`) })
                    )}
                />
                {tab === "history" && (
                    <ProviderHistory
                        key={provider.id}
                        providerId={provider.id}
                        version={provider.version}
                        catalogs={catalogs}
                    />
                )}
                {tab === "conflicts" && (
                    <ProviderConflictPanel
                        key={provider.id}
                        provider={provider}
                        onView={onView}
                        onSaved={onSaved}
                    />
                )}
                {tab === "details" && (
                    <div className="flex flex-col gap-5">
                        {provider.bc_present && (
                            <Alert variant="info">
                                {t("providers.ui.bc_explanation")}
                            </Alert>
                        )}
                        {providerGroups.map((group) => (
                            <section
                                key={group.name}
                                className="rounded-xl border border-slate-200 bg-box p-5 dark:border-white/10"
                            >
                                <h3 className="mb-4 border-b border-slate-100 pb-3 text-sm font-semibold dark:border-white/10">
                                    {t(`providers.sections.${group.name}`)}
                                </h3>
                                <dl className="divide-y divide-slate-100 dark:divide-white/5">
                                    {group.fields.map((field) => (
                                        <div
                                            key={field.name}
                                            className="flex flex-col gap-1 py-3 sm:flex-row sm:gap-6"
                                        >
                                            <dt className="shrink-0 text-xs text-slate-500 sm:w-52 dark:text-zinc-400">
                                                {t(
                                                    `providers.fields.${field.name}`
                                                )}
                                                {provider.locked_fields.includes(
                                                    field.name
                                                ) && (
                                                    <span>
                                                        {" "}
                                                        ·{" "}
                                                        {t(
                                                            "providers.ui.bc_locked"
                                                        )}
                                                    </span>
                                                )}
                                            </dt>
                                            <dd className="min-w-0 whitespace-pre-wrap break-words text-sm font-medium">
                                                {displayValue(
                                                    field.name,
                                                    provider[field.name],
                                                    catalogs,
                                                    t
                                                )}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
